import { createPortal } from 'react-dom';
import { useEffect, useMemo, useRef, type JSX } from 'react';

import type { Blueprint } from '@rta/rta-blueprint-library';
import { Kitchen3d } from '../kitchen-3d';
import { Kitchen3dController } from '../controller';

export type HeadlessCaptureRendererProps = {
	blueprint: Blueprint;
	onReady: (controller: Kitchen3dController) => void;
	onError: (error: Error) => void;
	readyTimeoutMs?: number;
	assetsBaseUrl?: string;
	initialSkyKey?: string;
	initialFloorKey?: string;
};

const DEFAULT_READY_TIMEOUT_MS = 120000;

export const HeadlessCaptureRenderer = (props: HeadlessCaptureRendererProps): JSX.Element => {
	const { blueprint, onReady, onError, readyTimeoutMs = DEFAULT_READY_TIMEOUT_MS, assetsBaseUrl, initialSkyKey, initialFloorKey } = props;

	const onReadyRef = useRef(onReady);
	onReadyRef.current = onReady;

	const onErrorRef = useRef(onError);
	onErrorRef.current = onError;

	// Fresh controller per render — avoids stale WebGL program references from any previously mounted canvas
	const controller = useMemo(() => {
		const c = new Kitchen3dController(blueprint, { eventGroup: `headless-capture-${blueprint.id}-${Date.now()}` });
		if (assetsBaseUrl) {
			c.options.assetsBaseUrl = assetsBaseUrl;
		}
		// Seed sky/floor BEFORE Kitchen3d mounts so the initial scene build loads the requested textures
		// instead of the controller defaults ('sky/overcast' / 'floor/grid-tile').
		if (initialSkyKey) c.skyKey = initialSkyKey;
		if (initialFloorKey) c.floorKey = initialFloorKey;
		return c;
	}, [blueprint, assetsBaseUrl, initialSkyKey, initialFloorKey]);

	useEffect(() => {
		let cancelled = false;
		controller.waitForSceneReady({ timeoutMs: readyTimeoutMs })
			.then(() => {
				if (!cancelled) onReadyRef.current(controller);
			})
			.catch((err: unknown) => {
				if (!cancelled) onErrorRef.current(err instanceof Error ? err : new Error(String(err)));
			});
		return () => { cancelled = true; };
	}, [controller, readyTimeoutMs]);

	return createPortal(
		<div style={{ position: 'fixed', left: '-9999px', top: '0', width: '1280px', height: '960px', pointerEvents: 'none' }}>
			<Kitchen3d controller={controller} backgroundColor="#1a1a2e" />
		</div>,
		document.body
	) as JSX.Element;
};
